import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users, MapPin, ArrowRight, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { getInitials } from '@/utils/stringUtils';
import type { DiscoveryGroup } from '@/hooks/useDiscoveryEngine';

interface Props {
  groups: DiscoveryGroup[];
}

export const DiscoverGroupsWidget: React.FC<Props> = ({ groups }) => {
  const navigate = useNavigate();

  if (groups.length === 0) return null;

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center">
            <Users className="h-4 w-4 mr-2 text-primary" />
            Discover Groups
          </span>
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => navigate('/groups')}>
            See more <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {groups.map((group) => (
          <div
            key={group.id}
            className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
            onClick={() => navigate(`/groups/${group.id}`)}
          >
            <Avatar className="h-10 w-10">
              <AvatarImage src={group.avatarUrl || undefined} alt={group.name} />
              <AvatarFallback>{getInitials(group.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{group.name}</div>
              {group.description && (
                <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{group.description}</p>
              )}
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                <span className="flex items-center"><Users className="h-3 w-3 mr-1" />{group.memberCount}</span>
                {group.location && (
                  <span className="flex items-center truncate"><MapPin className="h-3 w-3 mr-1" />{group.location}</span>
                )}
                {group.lastActivity && (
                  <span className="flex items-center text-primary/70">
                    <Clock className="h-3 w-3 mr-1" />
                    {formatDistanceToNow(new Date(group.lastActivity), { addSuffix: true })}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {group.matchReasons.slice(0, 2).map((reason) => (
                  <Badge key={reason} variant="secondary" className="text-[10px] px-1.5 py-0">
                    {reason}
                  </Badge>
                ))}
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  {Math.round(group.score * 100)}% match
                </Badge>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
